import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLocales, setLocale, type LocaleOption } from './index';

/**
 * The language control, on the public shell and on the retailer portal.
 *
 * The list comes from the API, so a language an administrator adds on A-34
 * appears here without a release — that is the whole point of holding languages
 * as data.
 *
 * A `<select>` rather than a row of flags. A flag is a country, not a language:
 * Arabic is spoken across the whole of Global's primary market and belongs to no
 * single one, and the country pack already carries a flag emoji for the place it
 * *is* the right symbol for. Each option is written in its own language, because
 * somebody looking for Arabic is not reading the word "Arabic".
 *
 * Deliberately absent from the admin console: staff work in English by decision,
 * so offering them a switcher would promise something the console does not do.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState<LocaleOption[]>([]);

  useEffect(() => {
    let live = true;
    void fetchLocales().then((l) => { if (live) setOptions(l); });
    return () => { live = false; };
  }, []);

  // One language is not a choice. Until a second is published the control would
  // be a dropdown with nothing in it, which reads as broken rather than as
  // "English only".
  if (options.length === 0) return null;

  const current = i18n.resolvedLanguage ?? 'en';

  return (
    <label className="mk-lang">
      <span className="mk-lang-lbl">{t('common.language')}</span>
      <select
        value={options.some((o) => o.code === current) ? current : 'en'}
        aria-label={t('common.language')}
        onChange={(e) => {
          const picked = options.find((o) => o.code === e.target.value);
          if (picked) void setLocale(picked.code, picked.direction);
        }}
      >
        {/* English is always offered and never fetched: it is the source text. */}
        <option value="en" lang="en">English</option>
        {options.filter((o) => o.code !== 'en').map((o) => (
          <option key={o.code} value={o.code} lang={o.code}>{o.endonym}</option>
        ))}
      </select>
    </label>
  );
}
